/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component } from 'react';
import { View, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';

import formatCurrency from '../core/support/formatCurrency';
import connectData, { ConnectedProps } from '../support/connectData';
import PricingActions from '../support/PricingActions';
import { showDialog } from './App/AppDialog';

import { Button, Page, Text } from './uiold';
import { themeColors } from './uiold/Constants';

import PriceTier from './PricingEditor/PriceTier';
import { canUser } from '../support/shared/permissionHelpers';

import { Tier } from '../types/shared/Tier';

type State = {
  selectedTierID: string;
};

class PricingEditor extends Component<FullProps, State> {
  state: State = {
    selectedTierID: '',
  };

  render() {
    let { props } = this;
    let { user } = props;
    let isLoading =
      props.isLoading || !props.markets || !props.priceTiers || !props.formDef;
    let selectedTier = this._getSelectedTier();
    return (
      <Page isLoading={isLoading}>
        <Page.Header title={t('Pricing')}>
          {user && canUser(user, { marketsAndPricing: 'WRITE' }) && (
            <Button title={t('Add Price Tier')} onPress={this._addTier} />
          )}
        </Page.Header>
        <View style={styles.panel}>
          <View style={styles.tierList}>
            <ScrollView>{this._renderListItems(selectedTier)}</ScrollView>
          </View>
          <View style={styles.detailPanel}>
            {this._renderPriceTier(selectedTier)}
          </View>
        </View>
      </Page>
    );
  }

  _getSelectedTier = () => {
    let { priceTiers } = this.props;
    let { selectedTierID } = this.state;
    if (!priceTiers) {
      return;
    }
    return priceTiers.getByID(selectedTierID) || priceTiers.getByIndex(0);
  };

  _renderPriceTier = (selectedTier: Tier | null | undefined) => {
    if (!selectedTier) {
      return;
    }
    let { formDef, markets, user } = this.props;
    if (!formDef || !markets) {
      return null;
    }
    return (
      <PriceTier
        key={selectedTier.id}
        priceTier={selectedTier}
        formDef={formDef as any}
        markets={markets}
        onDelete={this._onDeletePress}
        onRevert={this.props.fetchAll}
        onSave={this._onSave}
        canEdit={canUser(user, { marketsAndPricing: 'WRITE' })}
      />
    );
  };

  _renderListItems = (selectedTier: Tier | null | undefined) => {
    let { account, priceTiers } = this.props;
    if (!account || !priceTiers) {
      return;
    }

    let selectedTierId = selectedTier ? selectedTier.id : null;
    let accountCurrency =
      (account.settings && account.settings.currency) || 'USD';
    return priceTiers.mapToArray((tier) => {
      let rootStyle = [
        styles.tierListItem,
        tier.id === selectedTierId && styles.selectedTierListItem,
      ];
      return (
        <TouchableOpacity
          activeOpacity={0.6}
          key={tier.id}
          onPress={() => this._selectTier(tier)}
          style={rootStyle}
        >
          <Text style={styles.tierName}>{tier.name}</Text>
          <Text style={styles.tierDetail}>
            {t('Signup/Membership Fee: {fee}', {
              fee: formatCurrency(tier.base_price, accountCurrency),
            })}
          </Text>
          <Text style={styles.tierDetail}>
            {t('Sales Tax/GST: {tax}%', { tax: tier.tax_rate })}
          </Text>
        </TouchableOpacity>
      );
    });
  };

  _onSave = () => {
    this.props.savePriceTiers();
  };

  _selectTier = (tier: Tier) => {
    let { selectedTierID } = this.state;
    if (tier.id === selectedTierID) {
      return;
    }
    if (selectedTierID === '' || !this.props.isDirty) {
      this.setState({
        selectedTierID: tier.id,
      });
      return;
    }
    const CANCEL = t('Cancel');
    const SAVE_CHANGES = t('Save Changes');
    showDialog(
      t('Save Changes?'),
      t('You have un-saved changes.'),
      [CANCEL, t('Discard Changes'), SAVE_CHANGES],
      (value) => {
        if (value === CANCEL) {
          return;
        }
        if (value === SAVE_CHANGES) {
          this.props.savePriceTiers();
        } else {
          this.props.fetchAll();
        }
        this.setState({
          selectedTierID: tier.id,
        });
      },
    );
  };

  _addTier = () => {
    let newTier = PricingActions.addTier('New Price');
    this.setState({ selectedTierID: newTier.id });
  };

  _onDeletePress = () => {
    const CANCEL = t('Cancel');
    showDialog(
      t('Delete Pricing?'),
      t('Are you sure you want to permanently delete this pricing?'),
      [CANCEL, 'Delete'],
      (value) => {
        if (value === CANCEL) {
          return;
        }
        this._deleteSelectedTier();
      },
    );
  };

  _deleteSelectedTier = () => {
    let tier = this._getSelectedTier();
    if (tier && tier.id !== '0') {
      PricingActions.removeTier(tier);
      this.props.savePriceTiers();
      this.setState({ selectedTierID: '' });
    }
  };
}

let styles = StyleSheet.create({
  panel: {
    flex: 1,
    flexDirection: 'row',
  },
  tierList: {
    width: '35%',
    marginRight: 20,
  },
  tierListItem: {
    outlineWidth: 0,
    marginVertical: 5,
    padding: 15,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.borderGrey,
    borderRadius: 5,
    backgroundColor: themeColors.white,
    cursor: 'pointer',
  },
  selectedTierListItem: {
    borderRadius: 5,
    borderColor: themeColors.withOpacity.blue50,
    backgroundColor: themeColors.withOpacity.blue8,
  },
  tierName: {
    fontSize: 15,
    fontWeight: '500',
    color: themeColors.listNameText,
    lineHeight: 22,
  },
  tierDetail: {
    fontSize: 13,
    color: themeColors.listDetailText,
    lineHeight: 22,
  },
  detailPanel: {
    flex: 1,
  },
});

const connector = connectData()(
  () => ({
    account: { dataStoreKey: 'account' },
    user: { dataStoreKey: 'user' },
    formDef: {
      dataStoreKey: 'forms/:id',
      params: ['signup'],
    },
    markets: { dataStoreKey: 'markets' },
    priceTiers: { dataStoreKey: 'priceTiers' },
  }),
  () => ({
    savePriceTiers: { dataStoreKey: 'priceTiers' },
  }),
);
type FullProps = ConnectedProps<typeof connector>;
export default connector(PricingEditor);
